<template>
	<div>
		<JuniperPageHeader back="/juniper" title="Juniper Settings" subtitle="Configure Juniper CMS integrations and tools" />

		<ul class="nav nav-tabs mt-3" role="tablist">
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'default' }"
					type="button"
					@click="activeTab = 'default'"
				>
					Default
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'github' }"
					type="button"
					@click="activeTab = 'github'"
				>
					<i class="fab fa-github me-1"></i> GitHub
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'cloudflare' }"
					type="button"
					@click="activeTab = 'cloudflare'"
				>
					<i class="fas fa-cloud me-1"></i> Cloudflare
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'plesk' }"
					type="button"
					@click="activeTab = 'plesk'"
				>
					<i class="fas fa-server me-1"></i> Plesk
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'ada' }"
					type="button"
					@click="activeTab = 'ada'"
				>
					<i class="fas fa-universal-access me-1"></i> ADA Compliance
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'ai' }"
					type="button"
					@click="activeTab = 'ai'"
				>
					<i class="fas fa-magic me-1"></i> AI
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'retention' }"
					type="button"
					@click="activeTab = 'retention'"
				>
					<i class="fas fa-history me-1"></i> Retention
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'pms' }"
					type="button"
					@click="activeTab = 'pms'"
				>
					<i class="fas fa-user-tie me-1"></i> PMs
				</button>
			</li>
			<li v-if="hasDevelopAccess" class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'developers' }"
					type="button"
					@click="activeTab = 'developers'"
				>
					<i class="fas fa-code me-1"></i> Developers
				</button>
			</li>
		</ul>

		<div class="js-tab-content">
			<!-- Default tab — system-wide defaults (engine, etc.) -->
			<div v-show="activeTab === 'default'">
				<div class="gh-layout">
					<aside class="gh-intro">
						<h2 class="gh-intro__title">Static Generation Engine</h2>
						<p class="gh-intro__desc">
							Which renderer new clients use to build their static output. Existing clients keep their current engine until you change them individually from <em>Client → Deploy → Settings</em>.
						</p>
					</aside>

					<section class="gh-accounts">
						<div class="ada-card">
							<div class="gh-field">
								<label>Default engine for new clients</label>
								<select v-model="defaultEngine" class="form-select">
									<option value="default">Default Engine</option>
									<option value="juniper-publish-engine">Juniper Publish Engine</option>
								</select>
							</div>

							<div class="gh-field">
								<label>Current adoption</label>
								<div v-if="loadingEngineStats" class="text-muted">Loading…</div>
								<div v-else class="def-stats">
									<div
										class="def-stats__row"
										:class="{ 'def-stats__row--active': defaultEngine === 'default' }"
									>
										<div class="def-stats__name">
											Default Engine
											<span v-if="defaultEngine === 'default'" class="def-stats__badge">default</span>
										</div>
										<div class="def-stats__count">
											{{ engineStats.default }}
											<span class="def-stats__count-label">client{{ engineStats.default === 1 ? '' : 's' }}</span>
										</div>
									</div>
									<div
										class="def-stats__row"
										:class="{ 'def-stats__row--active': defaultEngine === 'juniper-publish-engine' }"
									>
										<div class="def-stats__name">
											Juniper Publish Engine
											<span v-if="defaultEngine === 'juniper-publish-engine'" class="def-stats__badge">default</span>
										</div>
										<div class="def-stats__count">
											{{ engineStats['juniper-publish-engine'] }}
											<span class="def-stats__count-label">client{{ engineStats['juniper-publish-engine'] === 1 ? '' : 's' }}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				</div>

				<hr class="gh-divider" />

				<div class="gh-save-row">
					<button class="gh-save-btn" :disabled="savingDefaults" @click="saveDefaults">
						<i class="fas fa-save"></i>
						{{ savingDefaults ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
			</div>

			<!-- GitHub tab — intro (30%) + accordion list (70%), then full-width HR + Save -->
			<div v-show="activeTab === 'github'">
				<div v-if="loadingGithub" class="text-muted">Loading…</div>

				<template v-else>
				<div class="gh-layout">
					<!-- LEFT (30%): just heading + short description, no border -->
					<aside class="gh-intro">
						<h2 class="gh-intro__title">GitHub Accounts</h2>
						<p class="gh-intro__desc">
							Credentials used by the static publisher to create per-client repos and push generated HTML.
							Tokens are masked once saved — leave them as <code>__keep__</code> to keep the existing value.
						</p>
					</aside>

					<!-- RIGHT (70%): accordion list, or single Add-account CTA if empty -->
					<section class="gh-accounts">
						<div v-if="githubAccounts.length === 0" class="gh-create-card">
							<i class="fab fa-github gh-create-card__icon"></i>
							<p class="gh-create-card__desc">
								No GitHub accounts configured yet. Add one to start mirroring generated static into per-client repositories.
							</p>
							<a href="#" class="gh-add-link" @click.prevent="addGithubAccount">
								<span class="gh-add-link__circle"><i class="fas fa-plus"></i></span>
								Add account
							</a>
						</div>

						<template v-else>
							<ul class="gh-accordion">
								<li
									v-for="(acct, idx) in githubAccounts"
									:key="idx"
									class="gh-accordion__item"
									:class="{ 'gh-accordion__item--open': expandedIdx === idx }"
								>
									<button
										type="button"
										class="gh-accordion__head"
										:aria-expanded="expandedIdx === idx ? 'true' : 'false'"
										@click="toggleAccount(idx)"
									>
										<div class="gh-accordion__avatar">
											<i class="fab fa-github"></i>
										</div>
										<div class="gh-accordion__head-body">
											<div class="gh-accordion__name">{{ acct.name || 'Untitled account' }}</div>
											<div class="gh-accordion__meta">
												<span v-if="acct.org">{{ acct.org }}</span>
												<span v-else class="text-muted">no org set</span>
												<span class="gh-accordion__sep">·</span>
												<span>{{ acct.visibility }}</span>
											</div>
										</div>
										<span
											class="gh-dot"
											:class="dotClassFor(idx, acct)"
											:title="dotTitleFor(idx, acct)"
										></span>
										<i class="fas fa-chevron-down gh-accordion__chevron"></i>
									</button>

									<div v-show="expandedIdx === idx" class="gh-accordion__body">
										<div class="gh-section">
											<div class="gh-section__title">Account type</div>
											<div class="gh-type-toggle">
												<label
													class="gh-type-toggle__opt"
													:class="{ 'gh-type-toggle__opt--active': acct.account_type === 'organization' }"
												>
													<input
														v-model="acct.account_type"
														type="radio"
														value="organization"
													/>
													<i class="fas fa-building"></i>
													<span>Organization</span>
												</label>
												<label
													class="gh-type-toggle__opt"
													:class="{ 'gh-type-toggle__opt--active': acct.account_type === 'user' }"
												>
													<input
														v-model="acct.account_type"
														type="radio"
														value="user"
													/>
													<i class="fas fa-user"></i>
													<span>Personal</span>
												</label>
											</div>
										</div>

										<div class="gh-section">
											<div class="gh-section__title">Identity</div>
											<div class="gh-grid gh-grid--2">
												<div class="gh-field">
													<label>Name (label)</label>
													<input v-model="acct.name" type="text" class="form-control" placeholder="default" />
												</div>
												<div class="gh-field">
													<label>Repository visibility</label>
													<select v-model="acct.visibility" class="form-select">
														<option value="private">Private</option>
														<option value="public">Public</option>
													</select>
												</div>
											</div>
										</div>

										<div class="gh-section">
											<div class="gh-section__title">Authentication</div>
											<div class="gh-field">
												<label>{{ acct.account_type === 'user' ? 'GitHub username' : 'GitHub organization' }}</label>
												<input
													v-model="acct.org"
													type="text"
													class="form-control"
													:placeholder="acct.account_type === 'user' ? 'srdjank90' : 'HejDev'"
												/>
											</div>
											<div class="gh-field">
												<label>Personal Access Token</label>
												<input
													v-model="acct.token"
													type="password"
													class="form-control"
													placeholder="ghp_…"
													autocomplete="new-password"
												/>
												<div class="gh-help">
													Needs scopes <code>repo</code> + <code>admin:org</code>. Leave as <code>__keep__</code> to keep the existing token.
												</div>
											</div>
										</div>


										<div class="gh-accordion__actions">
											<button
												class="btn btn-outline-secondary btn-sm"
												:disabled="testingIdx === idx"
												@click="testAccount(idx)"
											>
												<i
													class="fas me-1"
													:class="testingIdx === idx ? 'fa-spinner fa-spin' : 'fa-check-circle'"
												></i>
												{{ testingIdx === idx ? 'Testing…' : 'Test connection' }}
											</button>
											<button class="btn btn-outline-danger btn-sm" @click="removeGithubAccount(idx)">
												<i class="fas fa-trash me-1"></i> Remove account
											</button>
										</div>
									</div>
								</li>
							</ul>

							<div class="gh-add-link-row">
								<a href="#" class="gh-add-link" @click.prevent="addGithubAccount">
									<span class="gh-add-link__circle"><i class="fas fa-plus"></i></span>
									Add account
								</a>
							</div>
						</template>
					</section>
				</div>

				<!-- Full-width divider that separates the GitHub-accounts section from the Save action. -->
				<hr class="gh-divider" />

				<div class="gh-save-row">
					<button class="gh-save-btn" :disabled="savingGithub" @click="saveGithubAccounts">
						<i class="fas fa-save"></i>
						{{ savingGithub ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
				</template>
			</div>

			<!-- Cloudflare tab — accordion of CF accounts (same shape as GitHub tab) -->
			<div v-show="activeTab === 'cloudflare'">
				<div v-if="loadingCloudflare" class="text-muted">Loading…</div>

				<template v-else>
				<div class="gh-layout">
					<aside class="gh-intro">
						<h2 class="gh-intro__title">Cloudflare Accounts</h2>
						<p class="gh-intro__desc">
							Credentials used to provision per-client Cloudflare Pages projects from the GitHub mirror repo. Tokens are masked once saved — leave as <code>__keep__</code> to keep the existing value.
						</p>
					</aside>

					<section class="gh-accounts">
						<div v-if="cloudflareAccounts.length === 0" class="gh-create-card">
							<i class="fas fa-cloud gh-create-card__icon"></i>
							<p class="gh-create-card__desc">
								No Cloudflare accounts configured yet. Add one to start provisioning Pages projects for clients.
							</p>
							<a href="#" class="gh-add-link" @click.prevent="addCloudflareAccount">
								<span class="gh-add-link__circle"><i class="fas fa-plus"></i></span>
								Add account
							</a>
						</div>

						<template v-else>
							<ul class="gh-accordion">
								<li
									v-for="(acct, idx) in cloudflareAccounts"
									:key="idx"
									class="gh-accordion__item"
									:class="{ 'gh-accordion__item--open': cloudflareExpandedIdx === idx }"
								>
									<button
										type="button"
										class="gh-accordion__head"
										:aria-expanded="cloudflareExpandedIdx === idx ? 'true' : 'false'"
										@click="toggleCloudflareAccount(idx)"
									>
										<div class="gh-accordion__avatar">
											<i class="fas fa-cloud"></i>
										</div>
										<div class="gh-accordion__head-body">
											<div class="gh-accordion__name">{{ acct.name || 'Untitled account' }}</div>
											<div class="gh-accordion__meta">
												<span v-if="acct.account_id">{{ truncateMid(acct.account_id, 16) }}</span>
												<span v-else class="text-muted">no account ID set</span>
											</div>
										</div>
										<span
											class="gh-dot"
											:class="cloudflareDotClassFor(acct)"
											:title="cloudflareDotTitleFor(acct)"
										></span>
										<i class="fas fa-chevron-down gh-accordion__chevron"></i>
									</button>

									<div v-show="cloudflareExpandedIdx === idx" class="gh-accordion__body">
										<div class="gh-section">
											<div class="gh-section__title">Identity</div>
											<div class="gh-field">
												<label>Name (label)</label>
												<input v-model="acct.name" type="text" class="form-control" placeholder="default" />
											</div>
										</div>

										<div class="gh-section">
											<div class="gh-section__title">Cloudflare API</div>
											<div class="gh-field">
												<label>Account ID</label>
												<input
													v-model="acct.account_id"
													type="text"
													class="form-control"
													placeholder="e.g. 9a7b2c..."
													autocomplete="off"
												/>
												<div class="gh-help">Cloudflare dashboard → right sidebar → Account ID.</div>
											</div>
											<div class="gh-field">
												<label>API token</label>
												<input
													v-model="acct.api_token"
													type="password"
													class="form-control"
													placeholder="cf_…"
													autocomplete="new-password"
												/>
												<div class="gh-help">
													Needs <code>Account · Cloudflare Pages · Edit</code> + <code>Account · Account Settings · Read</code>. For the Cloudflare R2 target add <code>Account · Workers KV Storage · Edit</code>, <code>Zone · Workers Routes · Edit</code>, <code>Zone · Zone · Read</code>, <code>Zone · DNS · Edit</code> on all zones of the account. Leave as <code>__keep__</code> to keep existing.
												</div>
											</div>
										</div>

										<div class="gh-section">
											<div class="gh-section__title">R2 static hosting (deploy target “Cloudflare R2”)</div>
											<div class="gh-help mb-2">
												Optional. Only needed for clients whose deploy target is <strong>Cloudflare R2</strong>: the generated static is synced into this bucket under <code>clients/&lt;host&gt;/</code> and served by the juniper-edge Worker. Create an R2 API token with <em>Object Read &amp; Write</em> scoped to the bucket.
											</div>
											<div class="gh-field">
												<label>R2 bucket</label>
												<input
													v-model="acct.r2_bucket"
													type="text"
													class="form-control"
													placeholder="juniper-static"
													autocomplete="off"
												/>
											</div>
											<div class="gh-field">
												<label>R2 access key ID</label>
												<input
													v-model="acct.r2_access_key_id"
													type="text"
													class="form-control"
													placeholder="S3 API access key id"
													autocomplete="off"
												/>
											</div>
											<div class="gh-field">
												<label>R2 secret access key</label>
												<input
													v-model="acct.r2_secret_access_key"
													type="password"
													class="form-control"
													placeholder="S3 API secret"
													autocomplete="new-password"
												/>
												<div class="gh-help">Leave as <code>__keep__</code> to keep existing.</div>
											</div>
											<div class="gh-field">
												<label>R2 endpoint <span class="text-muted">(optional)</span></label>
												<input
													v-model="acct.r2_endpoint"
													type="text"
													class="form-control"
													:placeholder="acct.account_id ? 'https://' + acct.account_id + '.r2.cloudflarestorage.com' : 'https://<account id>.r2.cloudflarestorage.com'"
													autocomplete="off"
												/>
												<div class="gh-help">Derived from the Account ID when empty. Set only for jurisdiction-specific endpoints (e.g. <code>…r2.cloudflarestorage.com/eu</code>).</div>
											</div>
											<div class="gh-field">
												<label>Edge Worker name</label>
												<input
													v-model="acct.edge_worker_name"
													type="text"
													class="form-control"
													placeholder="juniper-edge"
													autocomplete="off"
												/>
												<div class="gh-help">The Worker that serves R2 clients. Routes for each client host are created against this script. Defaults to <code>juniper-edge</code>.</div>
											</div>
											<div class="gh-field">
												<label>Edge KV namespace ID (HOSTS)</label>
												<input
													v-model="acct.edge_kv_namespace_id"
													type="text"
													class="form-control"
													placeholder="32-char namespace id"
													autocomplete="off"
												/>
												<div class="gh-help">Workers KV namespace the Worker reads host → folder mappings from (<code>HOSTS</code> binding in <code>wrangler.jsonc</code>). Required for automatic edge wiring when a client switches to Cloudflare R2.</div>
											</div>
										</div>

										<div class="gh-accordion__actions">
											<button
												class="btn btn-outline-secondary btn-sm"
												:disabled="testingCloudflareIdx === idx"
												@click="testCloudflareAccount(idx)"
											>
												<i
													class="fas me-1"
													:class="testingCloudflareIdx === idx ? 'fa-spinner fa-spin' : 'fa-check-circle'"
												></i>
												{{ testingCloudflareIdx === idx ? 'Testing…' : 'Test connection' }}
											</button>
											<button
												class="btn btn-outline-secondary btn-sm"
												:disabled="testingR2Idx === idx || !acct.r2_bucket"
												:title="!acct.r2_bucket ? 'Fill in the R2 bucket first' : 'Check the S3 API keys against the bucket'"
												@click="testCloudflareR2Account(idx)"
											>
												<i
													class="fas me-1"
													:class="testingR2Idx === idx ? 'fa-spinner fa-spin' : 'fa-database'"
												></i>
												{{ testingR2Idx === idx ? 'Testing R2…' : 'Test R2 bucket' }}
											</button>
											<button class="btn btn-outline-danger btn-sm" @click="removeCloudflareAccount(idx)">
												<i class="fas fa-trash me-1"></i> Remove account
											</button>
										</div>
									</div>
								</li>
							</ul>

							<div class="gh-add-link-row">
								<a href="#" class="gh-add-link" @click.prevent="addCloudflareAccount">
									<span class="gh-add-link__circle"><i class="fas fa-plus"></i></span>
									Add account
								</a>
							</div>
						</template>
					</section>
				</div>

				<hr class="gh-divider" />

				<div class="gh-save-row">
					<button class="gh-save-btn" :disabled="savingCloudflare" @click="saveCloudflareAccounts">
						<i class="fas fa-save"></i>
						{{ savingCloudflare ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
				</template>
			</div>

			<!-- Plesk tab — accordion of Plesk accounts (same shape as GitHub/Cloudflare tabs) -->
			<div v-show="activeTab === 'plesk'">
				<div v-if="loadingPlesk" class="text-muted">Loading…</div>

				<template v-else>
				<div class="gh-layout">
					<aside class="gh-intro">
						<h2 class="gh-intro__title">Plesk Accounts</h2>
						<p class="gh-intro__desc">
							REST/CLI credentials for Plesk servers that host client subscriptions. Tokens are masked once saved — leave as <code>__keep__</code> to keep the existing value.
						</p>
					</aside>

					<section class="gh-accounts">
						<div v-if="pleskAccounts.length === 0" class="gh-create-card">
							<i class="fas fa-server gh-create-card__icon"></i>
							<p class="gh-create-card__desc">
								No Plesk accounts configured yet. Add one to wire clients to specific Plesk servers.
							</p>
							<a href="#" class="gh-add-link" @click.prevent="addPleskAccount">
								<span class="gh-add-link__circle"><i class="fas fa-plus"></i></span>
								Add account
							</a>
						</div>

						<template v-else>
							<ul class="gh-accordion">
								<li
									v-for="(acct, idx) in pleskAccounts"
									:key="idx"
									class="gh-accordion__item"
									:class="{ 'gh-accordion__item--open': pleskExpandedIdx === idx }"
								>
									<button
										type="button"
										class="gh-accordion__head"
										:aria-expanded="pleskExpandedIdx === idx ? 'true' : 'false'"
										@click="togglePleskAccount(idx)"
									>
										<div class="gh-accordion__avatar">
											<i class="fas fa-server"></i>
										</div>
										<div class="gh-accordion__head-body">
											<div class="gh-accordion__name">{{ acct.name || 'Untitled account' }}</div>
											<div class="gh-accordion__meta">
												<span v-if="acct.cli_url">{{ shortPleskUrl(acct.cli_url) }}</span>
												<span v-else class="text-muted">no CLI URL set</span>
											</div>
										</div>
										<span
											class="gh-dot"
											:class="pleskDotClassFor(acct)"
											:title="pleskDotTitleFor(acct)"
										></span>
										<i class="fas fa-chevron-down gh-accordion__chevron"></i>
									</button>

									<div v-show="pleskExpandedIdx === idx" class="gh-accordion__body">
										<div class="gh-section">
											<div class="gh-section__title">Identity</div>
											<div class="gh-field">
												<label>Name (label)</label>
												<input v-model="acct.name" type="text" class="form-control" placeholder="primary" />
											</div>
										</div>

										<div class="gh-section">
											<div class="gh-section__title">Plesk API</div>
											<div class="gh-grid gh-grid--2">
												<div class="gh-field">
													<label>CLI URL</label>
													<input
														v-model="acct.cli_url"
														type="text"
														class="form-control"
														placeholder="https://twjuniper.townweb.com/api/v2/cli/"
														autocomplete="off"
													/>
													<div class="gh-help">Plesk REST/CLI endpoint. Usually ends with <code>/api/v2/cli/</code>.</div>
												</div>
												<div class="gh-field">
													<label>Server IP</label>
													<input
														v-model="acct.ip"
														type="text"
														class="form-control"
														placeholder="5.161.88.82"
														autocomplete="off"
													/>
													<div class="gh-help">Public IP of the Plesk server. Used for FTP uploads to subscriptions on this server.</div>
												</div>
											</div>
											<div class="gh-field">
												<label>Auth token (base64 of admin:password)</label>
												<input
													v-model="acct.auth_token"
													type="password"
													class="form-control"
													placeholder="YWRtaW46…"
													autocomplete="new-password"
												/>
												<div class="gh-help">
													Generate with <code>echo -n "admin:PASSWORD" | base64</code>. Leave as <code>__keep__</code> to keep existing.
												</div>
											</div>
										</div>

										<div class="gh-accordion__actions">
											<button
												class="btn btn-outline-secondary btn-sm"
												:disabled="testingPleskIdx === idx"
												@click="testPleskAccount(idx)"
											>
												<i
													class="fas me-1"
													:class="testingPleskIdx === idx ? 'fa-spinner fa-spin' : 'fa-check-circle'"
												></i>
												{{ testingPleskIdx === idx ? 'Testing…' : 'Test connection' }}
											</button>
											<button class="btn btn-outline-danger btn-sm" @click="removePleskAccount(idx)">
												<i class="fas fa-trash me-1"></i> Remove account
											</button>
										</div>
									</div>
								</li>
							</ul>

							<div class="gh-add-link-row">
								<a href="#" class="gh-add-link" @click.prevent="addPleskAccount">
									<span class="gh-add-link__circle"><i class="fas fa-plus"></i></span>
									Add account
								</a>
							</div>
						</template>
					</section>
				</div>

				<hr class="gh-divider" />

				<div class="gh-save-row">
					<button class="gh-save-btn" :disabled="savingPlesk" @click="savePleskAccounts">
						<i class="fas fa-save"></i>
						{{ savingPlesk ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
				</template>
			</div>

			<!-- ADA Compliance tab — WAVE API key configuration, same 30/70 shell as GitHub -->
			<div v-show="activeTab === 'ada'">
				<div class="gh-layout">
					<!-- LEFT (30%): intro -->
					<aside class="gh-intro">
						<h2 class="gh-intro__title">ADA Compliance</h2>
						<p class="gh-intro__desc">
							Configure the WAVE accessibility audit API used to scan pages and PDFs for ADA compliance issues.
							Key is stored masked — leave the input empty to keep the existing value.
						</p>
					</aside>

					<!-- RIGHT (70%): WAVE config card -->
					<section class="gh-accounts">
						<div class="ada-card">
							<div class="gh-section">
								<div class="gh-section__title">WAVE API</div>

								<div class="gh-field">
									<label>API key</label>
									<div class="ada-input-wrap">
										<input
											v-model="waveApiKey"
											:type="showApiKey ? 'text' : 'password'"
											class="form-control"
											:placeholder="waveApiKeySet ? '•••••• (configured — leave empty to keep)' : 'Enter your WAVE API key'"
											autocomplete="off"
										/>
										<button
											type="button"
											class="ada-input-action"
											:title="showApiKey ? 'Hide' : 'Show'"
											@click="showApiKey = !showApiKey"
										>
											<i :class="showApiKey ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
										</button>
									</div>
									<div class="gh-help">
										Get your API key from
										<a href="https://wave.webaim.org/api/" target="_blank" rel="noopener">wave.webaim.org/api</a>.
									</div>
								</div>

								<div class="gh-field">
									<label>Credits remaining</label>
									<div class="ada-credits">
										<template v-if="waveCredits !== null">
											<span class="gh-dot" :class="creditsDotClass"></span>
											<span class="ada-credits__count">{{ waveCredits }}</span>
											<span class="ada-credits__label">credits</span>
										</template>
										<span v-else class="text-muted">Unknown — run an audit with WAVE to refresh</span>
									</div>
								</div>
							</div>
							<div class="gh-section">
								<div class="gh-section__title">Auditor service endpoints</div>

								<div class="gh-field">
									<label>ADA Engine API endpoint</label>
									<input
										v-model="adaEngineUrl"
										type="text"
										class="form-control"
										placeholder="https://ada-engine-634591367245.us-central1.run.app"
										autocomplete="off"
										spellcheck="false"
									/>
									<div class="gh-help">The <code>ada-engine</code> service that runs both IBM and AXE webpage scans (the <code>/ibm/scan-url</code> and <code>/axe/scan-url</code> routes are derived from it). Leave empty to use the server default (<code>ADA_ENGINE_URL</code>).</div>
								</div>

								<div class="gh-field">
									<label>Files (PDF) API endpoint</label>
									<input
										v-model="pdfAuditorUrl"
										type="text"
										class="form-control"
										placeholder="http://178.156.176.197:3000/audit"
										autocomplete="off"
										spellcheck="false"
									/>
									<div class="gh-help">Base endpoint for PDF (veraPDF) scans - the <code>/pdf/scan-url</code> route is derived from it. Leave empty for the server default (<code>PDF_AUDITOR_URL</code>).</div>
								</div>
							</div>

							<div class="gh-section">
								<div class="gh-section__title">Launch blockers</div>

								<div class="gh-field">
									<label>Disable "Launch" when the latest ADA audit has</label>
									<label v-for="b in launchBlockerOptions" :key="b.key" class="launch-blocker">
										<input v-model="launchBlockers[b.key]" type="checkbox" />
										<span>{{ b.label }}</span>
									</label>
									<label class="launch-blocker launch-blocker--scope">
										<input v-model="launchBlockers.unified_only" type="checkbox" />
										<span>Unified Dashboard clients only</span>
									</label>
									<div class="gh-help">Checked items disable the Launch button in the client's launch modal, based on the latest finished ADA audit. With "Unified Dashboard clients only", Old (Town Web) clients can always launch.</div>
								</div>
							</div>

							<div class="gh-section">
								<div class="gh-section__title">Slack notifications</div>

								<div class="gh-field">
									<label>Slack webhook URL</label>
									<input
										v-model="adaSlackWebhookUrl"
										type="text"
										class="form-control"
										placeholder="https://hooks.slack.com/services/T000/B000/xxxx"
										autocomplete="off"
										spellcheck="false"
									/>
									<div class="gh-help">Incoming-webhook URL of the Slack channel that receives a summary after every finished audit (for clients with Slack notifications enabled in their Audits tab). Create one at <code>api.slack.com/apps</code> &rarr; your app &rarr; Incoming Webhooks. Falls back to <code>ADA_SLACK_WEBHOOK_URL</code>.</div>
								</div>
							</div>
						</div>
					</section>
				</div>

				<!-- Full-width divider + Save changes — same as GitHub tab -->
				<hr class="gh-divider" />

				<div class="gh-save-row">
					<button class="gh-save-btn" :disabled="savingWave" @click="saveWaveSettings">
						<i class="fas fa-save"></i>
						{{ savingWave ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
			</div>

			<!-- AI tab — provider keys + the customizer's AI Theme Assistant (model, prompt).
				Everything lives in juniper-api (`ai_providers`, `ai_theme_assistant` settings),
				which also runs the assistant and falls back to its own .env. -->
			<div v-show="activeTab === 'ai'">
				<!-- Block 1: provider credentials shared by all AI features -->
				<div class="gh-layout">
					<aside class="gh-intro">
						<h2 class="gh-intro__title">AI Providers</h2>
						<p class="gh-intro__desc">
							Default API keys for OpenAI and Anthropic, used by every AI feature in mc2.
							Keys are stored masked — leave a key empty to keep the existing value. When no key is saved here, juniper-api falls back to its server <code>.env</code>.
						</p>
					</aside>

					<section class="gh-accounts">
						<div class="ada-card">
							<!-- Default provider credentials -->
							<div class="gh-section">
								<div class="gh-section__title">AI Providers <span class="gh-section__hint">— default keys for all AI features</span></div>

								<div class="gh-grid gh-grid--2">
									<div v-for="p in aiProviderList" :key="p.id" class="gh-field">
										<label>{{ p.label }} API key</label>
										<div class="ada-input-wrap">
											<input
												v-model="aiKeys[p.id]"
												:type="showAiKey[p.id] ? 'text' : 'password'"
												class="form-control"
												:placeholder="aiKeysClear[p.id] ? 'Key will be removed on save' : aiKeysSet[p.id] ? '•••••• (configured — leave empty to keep)' : 'Enter ' + p.label + ' API key'"
												:disabled="aiKeysClear[p.id]"
												autocomplete="off"
												spellcheck="false"
											/>
											<button
												type="button"
												class="ada-input-action"
												:title="showAiKey[p.id] ? 'Hide' : 'Show'"
												@click="$set(showAiKey, p.id, !showAiKey[p.id])"
											>
												<i :class="showAiKey[p.id] ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
											</button>
										</div>
										<div class="gh-help ai-key-status">
											<span class="gh-dot" :class="aiKeyDotClass(p.id)"></span>
											<span>{{ aiKeySourceLabel(p.id) }}.</span>
											<a v-if="aiKeysSet[p.id] && !aiKeysClear[p.id]" href="#" @click.prevent="clearAiKey(p.id)">Remove key</a>
											<a v-else-if="aiKeysClear[p.id]" href="#" @click.prevent="undoClearAiKey(p.id)">Undo remove</a>
										</div>
										<div class="gh-help">Falls back to <code>{{ p.env }}</code> in juniper-api when empty.</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				</div>

				<!-- Block 2: the customizer's Theme Assistant (provider, model, prompt) -->
				<div class="gh-layout ai-layout">
					<aside class="gh-intro">
						<h2 class="gh-intro__title">AI Theme Assistant</h2>
						<p class="gh-intro__desc">
							The chat assistant in the Theme 2025 customizer that edits Figma variables on request.
							Choose the provider and model it runs on and tune its instructions. Empty fields use the juniper-api server defaults.
						</p>
					</aside>

					<section class="gh-accounts">
						<div class="ada-card">
							<!-- Theme Assistant: provider + model -->
							<div class="gh-section">
								<div class="gh-section__title">AI Theme Assistant <span class="gh-section__hint">— customizer, Theme 2025</span></div>
								<div v-if="aiMeta.effective && aiMeta.effective.provider" class="gh-help ai-effective">
									Currently active: <strong>{{ aiMeta.effective.provider }}</strong> / <code>{{ aiMeta.effective.model }}</code>
								</div>

								<div class="gh-field">
									<label>Provider</label>
									<div class="gh-type-toggle">
										<label
											v-for="p in aiProviderList"
											:key="p.id"
											class="gh-type-toggle__opt"
											:class="{ 'gh-type-toggle__opt--active': ai.provider === p.id }"
										>
											<input v-model="ai.provider" type="radio" :value="p.id" />
											<i :class="p.icon"></i> {{ p.label }}
										</label>
									</div>
								</div>

								<div v-if="ai.provider === 'openai'" class="gh-grid gh-grid--2">
									<div class="gh-field">
										<label>Model</label>
										<input
											v-model="ai.openai_model"
											list="ai-openai-models"
											class="form-control"
											:placeholder="'Default: ' + (aiMeta.defaults.openai_model || '')"
											autocomplete="off"
											spellcheck="false"
										/>
										<div class="gh-help">
											Pick from the live OpenAI list or type an id. Recommended: <code>gpt-5.6-terra</code>.
											<span v-if="aiModelsError.openai" class="text-danger">{{ aiModelsError.openai }}</span>
										</div>
									</div>
									<div class="gh-field">
										<label>Fallback model</label>
										<input
											v-model="ai.openai_fallback_model"
											list="ai-openai-models"
											class="form-control"
											placeholder="None — leave empty to disable"
											autocomplete="off"
											spellcheck="false"
										/>
										<div class="gh-help">Tried once when the primary model returns an error (e.g. <code>gpt-5.6-luna</code>).</div>
									</div>
								</div>
								<div v-else class="gh-field">
									<label>Model</label>
									<input
										v-model="ai.anthropic_model"
										list="ai-anthropic-models"
										class="form-control"
										:placeholder="'Default: ' + (aiMeta.defaults.anthropic_model || '')"
										autocomplete="off"
										spellcheck="false"
									/>
									<div class="gh-help">
										Pick from the live Anthropic list or type an id. Recommended: <code>claude-opus-5</code>. Fable models work too, but do not accept forced tool calls — the assistant requests the tool through the prompt instead.
										<span v-if="aiModelsError.anthropic" class="text-danger">{{ aiModelsError.anthropic }}</span>
									</div>
								</div>
								<datalist id="ai-openai-models">
									<option v-for="m in aiModels.openai" :key="m" :value="m"></option>
								</datalist>
								<datalist id="ai-anthropic-models">
									<option v-for="m in aiModels.anthropic" :key="m" :value="m"></option>
								</datalist>

								<div class="gh-grid gh-grid--2">
									<div class="gh-field">
										<label>Max output tokens</label>
										<input
											v-model.number="ai.max_tokens"
											type="number"
											min="1024"
											max="128000"
											step="1024"
											class="form-control"
											:placeholder="'Default: ' + (aiMeta.defaults.max_tokens || '')"
										/>
										<div class="gh-help">Cap for one reply. A full redesign uses about 2k output tokens, so the default is plenty.</div>
									</div>
									<div class="gh-field">
										<label>Connection</label>
										<div class="ai-test-row">
											<button class="gh-save-btn gh-save-btn--sm" :disabled="testingAi" @click="testAiConnection">
												<i class="fas" :class="testingAi ? 'fa-spinner fa-spin' : 'fa-check-circle'"></i>
												{{ testingAi ? 'Testing…' : 'Test connection' }}
											</button>
											<span v-if="aiTestResult" class="ai-test-result" :class="{ 'ai-test-result--bad': !aiTestResult.ok }">
												<span class="gh-dot" :class="aiTestResult.ok ? 'gh-dot--on' : 'gh-dot--bad'"></span>
												{{ aiTestResult.message }}
											</span>
										</div>
										<div class="gh-help">Sends one tiny request with the saved key and the model above.</div>
									</div>
								</div>
							</div>

							<!-- Theme Assistant: system prompt -->
							<div class="gh-section">
								<div class="gh-section__title">
									System prompt
									<span v-if="aiPromptIsCustom" class="def-stats__badge">custom</span>
									<span v-else class="gh-section__hint">— default</span>
								</div>
								<div class="gh-field">
									<textarea v-model="ai.system_prompt" class="form-control ai-prompt" rows="16" spellcheck="false"></textarea>
									<div class="gh-help">
										Instructions the assistant receives before the variables block. Runtime safety rules are appended by the server and cannot be overridden.
										<a v-if="aiPromptIsCustom" href="#" @click.prevent="resetAiPrompt">Reset to default</a>
									</div>
								</div>
							</div>
						</div>
					</section>
				</div>

				<hr class="gh-divider" />

				<div class="gh-save-row">
					<button class="gh-save-btn" :disabled="savingAi || loadingAi" @click="saveAiSettings">
						<i class="fas fa-save"></i>
						{{ savingAi ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
			</div>

			<!-- Retention tab — automatic cleanup windows for history data -->
			<div v-show="activeTab === 'retention'">
				<div class="gh-layout">
					<aside class="gh-intro">
						<h2 class="gh-intro__title">Data Retention</h2>
						<p class="gh-intro__desc">
							How long history data is kept before the nightly cleanup deletes it.
							Runs every day from 04:00 — deployments, page revisions and activity logs in
							juniper, ADA audit reports in mc2. Live records (running builds, audits in
							progress, changes still waiting for a decision) are never touched.
							Use <b>Delete now…</b> to clear a section right away, picking how far back.
						</p>
					</aside>

					<section class="gh-accounts">
						<div class="ada-card">
							<div class="gh-field">
								<label><i class="fas fa-rocket me-1"></i> Deployments (static generations)</label>
								<select v-model.number="retentionDeploysDays" class="form-select">
									<option v-for="opt in retentionOptions" :key="'d-' + opt.days" :value="opt.days">{{ opt.label }}</option>
								</select>
								<div class="gh-help">Deployment history rows older than this are deleted automatically.</div>
								<button class="gh-save-btn gh-save-btn--sm gh-save-btn--danger mt-2" @click="openPurge('deploys')">
									<i class="fas fa-trash"></i> Delete now…
								</button>
							</div>

							<div class="gh-field">
								<label><i class="fas fa-universal-access me-1"></i> ADA audit reports</label>
								<select v-model.number="retentionAdaDays" class="form-select">
									<option v-for="opt in retentionOptions" :key="'a-' + opt.days" :value="opt.days">{{ opt.label }}</option>
								</select>
								<div class="gh-help">Audit reports (with all page scans) older than this are deleted automatically.</div>
								<button class="gh-save-btn gh-save-btn--sm gh-save-btn--danger mt-2" @click="openPurge('ada_reports')">
									<i class="fas fa-trash"></i> Delete now…
								</button>
							</div>

							<div class="gh-field">
								<label><i class="fas fa-code-branch me-1"></i> Page revisions</label>
								<select v-model.number="retentionPageRevisionsDays" class="form-select">
									<option v-for="opt in retentionOptions" :key="'p-' + opt.days" :value="opt.days">{{ opt.label }}</option>
								</select>
								<div class="gh-help">
									Decided page changes older than this are deleted automatically, with the page
									content they carried. Changes still waiting for a decision are never touched,
									however long they have been waiting.
								</div>
								<button class="gh-save-btn gh-save-btn--sm gh-save-btn--danger mt-2" @click="openPurge('page_revisions')">
									<i class="fas fa-trash"></i> Delete now…
								</button>
							</div>

							<div class="gh-field">
								<label><i class="fas fa-list-ul me-1"></i> Activity logs</label>
								<select v-model.number="retentionActivityLogsDays" class="form-select">
									<option v-for="opt in retentionOptions" :key="'l-' + opt.days" :value="opt.days">{{ opt.label }}</option>
								</select>
								<div class="gh-help">
									Activity log entries (who changed what, with IP and device) older than this are
									deleted automatically, for every client.
								</div>
								<button class="gh-save-btn gh-save-btn--sm gh-save-btn--danger mt-2" @click="openPurge('activity_logs')">
									<i class="fas fa-trash"></i> Delete now…
								</button>
							</div>
						</div>
					</section>
				</div>

				<hr class="gh-divider" />

				<div class="gh-save-row">
					<button class="gh-save-btn" :disabled="savingRetention" @click="saveRetention">
						<i class="fas fa-save"></i>
						{{ savingRetention ? 'Saving…' : 'Save changes' }}
					</button>
				</div>

				<ConfirmModal
					ref="purgeModal"
					:title="'Delete ' + purgeSectionLabel"
					confirm-text="Delete"
					confirm-icon="fa-trash"
					icon-class="fa-trash"
					danger
					:busy="purging"
					@confirm="executePurge"
				>
					<p class="mb-2">
						Deletes {{ purgeSectionLabel.toLowerCase() }} for <b>all clients</b>. Live records are never
						deleted. This cannot be undone.
					</p>
					<label class="form-label mb-1">Age</label>
					<select v-model="purgeAge" class="form-select form-select-sm">
						<option value="">Everything (any age)</option>
						<option v-for="days in purgeAgeOptions" :key="'pa-' + days" :value="String(days)">
							Older than {{ days }} days
						</option>
					</select>
				</ConfirmModal>
			</div>

			<!-- PMs tab — who can be credited as project manager on a client -->
			<div v-show="activeTab === 'pms'">
				<div class="gh-layout">
					<aside class="gh-intro">
						<h2 class="gh-intro__title">Project Managers</h2>
						<p class="gh-intro__desc">
							Users checked here appear in the <em>Project Manager</em> dropdown on the client edit page
							and in the clients-list filter. Unchecking someone does not clear the clients already
							credited to them — their name keeps showing on those clients as "(unlisted)".
						</p>
					</aside>

					<section class="gh-accounts">
						<div class="ada-card">
							<div v-if="loadingPms" class="text-muted">Loading…</div>
							<template v-else>
								<div class="gh-section__title">
									Users
									<span class="pm-count">{{ pmSelectedIds.length }} selected</span>
								</div>
								<div class="pm-search">
									<i class="fas fa-search pm-search__icon"></i>
									<input
										type="text"
										class="form-control"
										v-model.trim="pmSearch"
										placeholder="Filter users…"
									/>
								</div>
								<div class="pm-list">
									<label v-for="user in filteredPmCandidates" :key="user.id" class="pm-row">
										<input
											type="checkbox"
											:checked="pmSelectedIds.includes(user.id)"
											@change="togglePm(user.id)"
										/>
										<img v-if="user.avatar" :src="user.avatar" class="pm-row__avatar" alt="" />
										<span v-else class="pm-row__avatar pm-row__avatar--placeholder">
											<i class="fas fa-user"></i>
										</span>
										<span class="pm-row__name">{{ user.name }}</span>
										<span class="pm-row__email">{{ user.email }}</span>
									</label>
									<div v-if="!filteredPmCandidates.length" class="text-muted pm-empty">No users match “{{ pmSearch }}”</div>
								</div>
							</template>
						</div>
					</section>
				</div>

				<hr class="gh-divider" />

				<div class="gh-save-row">
					<button class="gh-save-btn" :disabled="savingPms || loadingPms" @click="savePms">
						<i class="fas fa-save"></i>
						{{ savingPms ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
			</div>

			<!-- Developers tab — shell command runner (gated on `develop` permission) -->
			<div v-if="hasDevelopAccess" v-show="activeTab === 'developers'">
				<div class="gh-layout">
					<aside class="gh-intro">
						<h2 class="gh-intro__title">Developer Tools</h2>
						<p class="gh-intro__desc">
							Run shell commands inside the Juniper environment. Output appears in the console below. Use <code>cls</code> to clear the output.
						</p>
					</aside>

					<section class="gh-accounts">
						<div class="ada-card dev-runner">
							<div class="gh-section">
								<div class="gh-section__title">Command Runner</div>
								<div class="input-group">
									<input
										type="text"
										v-model="devCommand"
										@keyup.enter="executeDevCommand"
										class="form-control"
										placeholder="Type shell command…"
										aria-label="Type shell command"
										:disabled="devExecuting"
									/>
									<button
										class="btn btn-primary d-flex align-items-center"
										:class="{ disabled: devExecuting }"
										type="button"
										@click="executeDevCommand"
										:disabled="devExecuting"
									>
										<font-awesome-icon
											class="me-2"
											:icon="['fas', devExecuting ? 'spinner' : 'bolt']"
											:class="{ 'fa-spin': devExecuting }"
										/>
										<span>{{ devExecuting ? 'Executing' : 'Execute' }}</span>
									</button>
								</div>
								<div v-if="devCommandsHistory.length > 0" class="dev-history mt-3">
									<span
										v-for="(prev, index) in devCommandsHistory"
										:key="prev + index"
										class="dev-history__item"
										@click="devCommand = prev"
									>{{ prev }}</span>
								</div>
							</div>
						</div>

						<div class="dev-console mt-3">
							<div class="dev-console__head">
								<span>Shell Output</span>
								<button
									class="dev-console__clear"
									:disabled="devExecuting || !devResponse"
									@click="devResponse = ''"
									title="Clear output"
								>
									<font-awesome-icon :icon="['fas', 'eraser']" />
									<span>Clear</span>
								</button>
							</div>
							<pre class="dev-console__body" v-html="devResponse" ref="devShellContainer"></pre>
						</div>
					</section>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import api from '@/api'
import juniperApi from '@/api/juniperApi'
import { showGlobalToast } from '@/utils/toast'
import juniperUserMixin from '@/mixins/juniperUserMixin'
import ConfirmModal from '@/components/common/ConfirmModal.vue'

const SECRET_PLACEHOLDER = '__keep__'

function emptyGithubAccount() {
	return {
		account_type: 'organization',
		name: '',
		org: '',
		token: '',
		visibility: 'private',
	}
}

export default {
	name: 'JuniperSettings',
	components: { ConfirmModal },
	data() {
		return {
			activeTab: 'default',

			// Default tab — system-wide defaults applied to new clients.
			defaultEngine: 'default',
			// Retention tab — cleanup windows in days (default 1 year)
			retentionDeploysDays: 365,
			retentionAdaDays: 365,
			retentionPageRevisionsDays: 365,
			retentionActivityLogsDays: 365,
			savingRetention: false,
			// "Delete now…" modal — one at a time, for the section picked
			purgeSection: null,
			purgeAge: '365',
			purging: false,
			purgeAgeOptions: [7, 30, 60, 90, 180, 365],
			purgeSections: {
				deploys: 'Deployments',
				ada_reports: 'ADA audit reports',
				page_revisions: 'Page revisions',
				activity_logs: 'Activity logs',
			},
			retentionOptions: [
				{ days: 30, label: '30 days' },
				{ days: 60, label: '60 days' },
				{ days: 90, label: '90 days' },
				{ days: 180, label: '180 days' },
				{ days: 365, label: '1 year (365 days)' },
				{ days: 730, label: '2 years (730 days)' },
			],
			savingDefaults: false,
			loadingEngineStats: false,
			engineStats: { default: 0, 'juniper-publish-engine': 0, total: 0 },

			// Cloudflare tab — list of CF accounts, same shape as github_accounts.
			cloudflareAccounts: [],
			cloudflareExpandedIdx: null,
			loadingCloudflare: false,
			savingCloudflare: false,
			// Per-account test results, keyed by account name. 'success' | 'failed' | 'pending'
			cloudflareTestResults: {},
			testingCloudflareIdx: null,
			testingR2Idx: null,

			// Plesk tab — list of Plesk accounts (cli_url + auth_token), same shape.
			pleskAccounts: [],
			pleskExpandedIdx: null,
			loadingPlesk: false,
			savingPlesk: false,
			pleskTestResults: {},
			testingPleskIdx: null,

			// WAVE / ADA
			waveApiKey: '',
			waveApiKeySet: false,
			waveCredits: null,
			showApiKey: false,
			savingWave: false,
			// Auditor service endpoints (settable; empty = use env default)
			adaEngineUrl: '',
			pdfAuditorUrl: '',
			adaSlackWebhookUrl: '',
			launchBlockers: { unified_only: true, no_audit: true, theme: true, content: true, document: false },
			launchBlockerOptions: [
				{ key: 'no_audit', label: 'No finished ADA audit' },
				{ key: 'theme', label: 'Theme errors' },
				{ key: 'content', label: 'Content errors' },
				{ key: 'document', label: 'Files (PDF) errors' },
			],

			// AI tab — provider keys (`ai_providers`) + Theme Assistant config
			// (`ai_theme_assistant`), all in juniper-api. Keys round-trip masked as
			// SECRET_PLACEHOLDER; meta/models/test are juniper-api helper endpoints.
			aiProviderList: [
				{ id: 'openai', label: 'OpenAI', env: 'CHATGPT_API_KEY', icon: 'fas fa-bolt' },
				{ id: 'anthropic', label: 'Anthropic', env: 'ANTHROPIC_API_KEY', icon: 'fas fa-brain' },
			],
			aiKeys: { openai: '', anthropic: '' },
			aiKeysSet: { openai: false, anthropic: false },
			aiKeysClear: { openai: false, anthropic: false },
			showAiKey: { openai: false, anthropic: false },
			ai: {
				provider: 'openai',
				openai_model: '',
				openai_fallback_model: '',
				anthropic_model: '',
				max_tokens: null,
				system_prompt: '',
			},
			aiMeta: { default_system_prompt: '', defaults: {}, effective: {}, priced_models: { openai: [], anthropic: [] } },
			aiModels: { openai: [], anthropic: [] },
			aiModelsError: { openai: '', anthropic: '' },
			aiModelsLoaded: false,
			loadingAi: false,
			savingAi: false,
			testingAi: false,
			// { ok: bool, message: string } from POST /settings/ai/test
			aiTestResult: null,

			// GitHub
			githubAccounts: [],
			// Index of the currently expanded accordion item; null = all collapsed.
			expandedIdx: null,
			loadingGithub: false,
			savingGithub: false,
			// Connection test state — keyed by account `name` (stable across reorder).
			// Values: 'success' | 'failed' | 'pending'. Cleared on reload and when name changes.
			testResults: {},
			// Index of the account currently being tested; null when idle.
			testingIdx: null,
			// Set to true while the auto-test (on tab activation) is running.
			testingAll: false,

			// PMs tab — project-manager whitelist (mc2 users).
			pmCandidates: [],
			pmSelectedIds: [],
			pmSearch: '',
			loadingPms: false,
			savingPms: false,

			// Developers tab — shell command runner state.
			devCommand: '',
			devResponse: '',
			devCommandsHistory: [],
			devExecuting: false,
		}
	},

	computed: {
		purgeSectionLabel() {
			return this.purgeSections[this.purgeSection] || ''
		},
		// Credits-remaining dot color: green when comfortable, gray when low,
		// red when nearly empty. Reuses the gh-dot palette from GitHub tab.
		creditsDotClass() {
			if (this.waveCredits === null) return 'gh-dot--off'
			if (this.waveCredits > 100) return 'gh-dot--on'
			if (this.waveCredits > 20) return 'gh-dot--off'
			return 'gh-dot--bad'
		},

		hasDevelopAccess() {
			return this.userHasAllNeededJuniperPermissions(['develop'])
		},

		// AI tab helpers
		aiPromptIsCustom() {
			const prompt = (this.ai.system_prompt || '').trim()
			return prompt !== '' && prompt !== (this.aiMeta.default_system_prompt || '').trim()
		},
		aiCurrentModel() {
			const d = this.aiMeta.defaults || {}
			return this.ai.provider === 'anthropic'
				? (this.ai.anthropic_model || d.anthropic_model || '').trim()
				: (this.ai.openai_model || d.openai_model || '').trim()
		},
		aiHasUnsavedKey() {
			return !!(this.aiKeys.openai.trim() || this.aiKeys.anthropic.trim() || this.aiKeysClear.openai || this.aiKeysClear.anthropic)
		},

		filteredPmCandidates() {
			if (!this.pmSearch) return this.pmCandidates
			const q = this.pmSearch.toLowerCase()
			return this.pmCandidates.filter(
				user => (user.name || '').toLowerCase().includes(q) || (user.email || '').toLowerCase().includes(q)
			)
		},
	},

	watch: {
		// Re-test every saved account whenever the user lands on the relevant
		// tab. Cheap (~3 API calls per account, in parallel) and keeps the
		// header dots in sync with reality.
		activeTab: {
			handler(newTab) {
				if (newTab === 'github' && this.githubAccounts.length > 0) {
					this.testAllAccounts()
				}
				if (newTab === 'cloudflare' && this.cloudflareAccounts.length > 0) {
					this.testAllCloudflareAccounts()
				}
				if (newTab === 'plesk' && this.pleskAccounts.length > 0) {
					this.testAllPleskAccounts()
				}
				if (newTab === 'ai') {
					this.loadAiModels()
				}
			},
		},
		// Auto-scroll the dev shell output to the bottom as new lines arrive.
		devResponse() {
			this.$nextTick(() => {
				const el = this.$refs.devShellContainer
				if (el) el.scrollTop = el.scrollHeight
			})
		},
	},
	created() {
		// Deep link: /juniper/settings?tab=ai (used by the customizer's AI modal).
		const tab = this.$route && this.$route.query ? this.$route.query.tab : null
		const known = ['default', 'github', 'cloudflare', 'plesk', 'ada', 'ai', 'retention', 'pms', 'developers']
		if (tab && known.includes(tab)) {
			this.activeTab = tab
		}
	},
	async mounted() {
		await Promise.all([
			this.loadDefaults(),
			this.loadEngineStats(),
			this.loadWaveSettings(),
			this.loadGithubAccounts(),
			this.loadCloudflareSettings(),
			this.loadPleskSettings(),
			this.loadRetention(),
			this.loadPms(),
			this.loadAiSettings(),
		])
	},
	methods: {
		// ----- Defaults -----
		async loadDefaults() {
			try {
				const { data } = await juniperApi.get('/settings/default_engine')
				const known = ['default', 'juniper-publish-engine']
				this.defaultEngine = known.includes(data?.value) ? data.value : 'default'
			} catch (e) {
				if (e?.response?.status !== 404) {
					console.error('Failed to load default engine', e)
				}
				this.defaultEngine = 'default'
			}
		},
		async loadEngineStats() {
			this.loadingEngineStats = true
			try {
				const { data } = await juniperApi.get('/clients/engine-stats')
				this.engineStats = {
					default: data?.default ?? 0,
					'juniper-publish-engine': data?.['juniper-publish-engine'] ?? 0,
					total: data?.total ?? 0,
				}
			} catch (e) {
				console.error('Failed to load engine stats', e)
			} finally {
				this.loadingEngineStats = false
			}
		},
		async saveDefaults() {
			this.savingDefaults = true
			try {
				await juniperApi.put('/settings/default_engine', { value: this.defaultEngine })
				showGlobalToast('Defaults saved', 'success')
			} catch (e) {
				console.error(e)
				showGlobalToast('Failed to save defaults', 'danger')
			} finally {
				this.savingDefaults = false
			}
		},

		// ----- Retention -----
		async loadRetention() {
			try {
				const [deploys, ada, pageRevisions, activityLogs] = await Promise.all([
					juniperApi.get('/settings/retention_deploys_days'),
					juniperApi.get('/settings/retention_ada_reports_days'),
					juniperApi.get('/settings/retention_page_revisions_days'),
					juniperApi.get('/settings/retention_activity_logs_days'),
				])
				this.retentionDeploysDays = Number(deploys.data?.value) || 365
				this.retentionAdaDays = Number(ada.data?.value) || 365
				this.retentionPageRevisionsDays = Number(pageRevisions.data?.value) || 365
				this.retentionActivityLogsDays = Number(activityLogs.data?.value) || 365
			} catch (e) {
				console.error('Failed to load retention settings', e)
			}
		},
		async saveRetention() {
			this.savingRetention = true
			try {
				await Promise.all([
					juniperApi.put('/settings/retention_deploys_days', { value: this.retentionDeploysDays }),
					juniperApi.put('/settings/retention_ada_reports_days', { value: this.retentionAdaDays }),
					juniperApi.put('/settings/retention_page_revisions_days', {
						value: this.retentionPageRevisionsDays,
					}),
					juniperApi.put('/settings/retention_activity_logs_days', {
						value: this.retentionActivityLogsDays,
					}),
				])
				showGlobalToast('Retention settings saved', 'success')
			} catch (e) {
				console.error(e)
				showGlobalToast('Failed to save retention settings', 'danger')
			} finally {
				this.savingRetention = false
			}
		},
		openPurge(section) {
			this.purgeSection = section
			this.purgeAge = '365'
			this.$refs.purgeModal.open()
		},
		// Runs the section's prune command now (same rules as the nightly
		// cleanup), with the picked age; empty age = everything.
		async executePurge() {
			this.purging = true
			try {
				const payload = { section: this.purgeSection }
				if (this.purgeAge) payload.older_than_days = Number(this.purgeAge)
				const { data } = await juniperApi.post('/retention/purge', payload)
				const n = data.deleted || 0
				showGlobalToast(`Deleted ${n} ${n === 1 ? 'entry' : 'entries'} from ${this.purgeSectionLabel}`, 'success')
				this.$refs.purgeModal.close()
			} catch (e) {
				console.error(e)
				showGlobalToast(e?.response?.data?.message || 'Delete failed', 'danger')
			} finally {
				this.purging = false
			}
		},

		// ----- PMs (project-manager whitelist, stored in mc2) -----
		async loadPms() {
			this.loadingPms = true
			try {
				const [candidates, current] = await Promise.all([
					api.get('users/project-managers/candidates'),
					api.get('users/project-managers'),
				])
				this.pmCandidates = candidates.data || []
				this.pmSelectedIds = (current.data || []).map(user => user.id)
			} catch (e) {
				console.error('Failed to load project managers', e)
			} finally {
				this.loadingPms = false
			}
		},
		togglePm(id) {
			if (this.pmSelectedIds.includes(id)) {
				this.pmSelectedIds = this.pmSelectedIds.filter(existing => existing !== id)
			} else {
				this.pmSelectedIds = [...this.pmSelectedIds, id]
			}
		},
		async savePms() {
			this.savingPms = true
			try {
				const { data } = await api.put('users/project-managers', { user_ids: this.pmSelectedIds })
				// Refresh the session cache so client edit pages pick the new list
				// up without a reload (fetchProjectManagers caches it once).
				this.$store.commit('SET_PROJECT_MANAGERS', data || [])
				showGlobalToast('Project managers saved', 'success')
			} catch (e) {
				console.error(e)
				showGlobalToast('Failed to save project managers', 'danger')
			} finally {
				this.savingPms = false
			}
		},

		// ----- Cloudflare accounts -----
		async loadCloudflareSettings() {
			this.loadingCloudflare = true
			try {
				const { data } = await juniperApi.get('/settings/cloudflare_accounts')
				const value = data && data.value
				this.cloudflareAccounts = Array.isArray(value) ? value.map(this.normalizeCloudflareAccount) : []
				if (this.cloudflareExpandedIdx !== null && this.cloudflareExpandedIdx >= this.cloudflareAccounts.length) {
					this.cloudflareExpandedIdx = null
				}
			} catch (e) {
				if (!e || !e.response || e.response.status !== 404) {
					console.error('Failed to load Cloudflare accounts', e)
				}
				this.cloudflareAccounts = []
				this.cloudflareExpandedIdx = null
			} finally {
				this.loadingCloudflare = false
			}
			if (this.activeTab === 'cloudflare' && this.cloudflareAccounts.length > 0) {
				this.testAllCloudflareAccounts()
			}
		},
		normalizeCloudflareAccount(a) {
			return Object.assign(
				{
					name: '', account_id: '', api_token: '',
					r2_bucket: '', r2_access_key_id: '', r2_secret_access_key: '', r2_endpoint: '',
					edge_worker_name: '', edge_kv_namespace_id: '',
				},
				a || {}
			)
		},
		toggleCloudflareAccount(idx) {
			this.cloudflareExpandedIdx = this.cloudflareExpandedIdx === idx ? null : idx
		},
		addCloudflareAccount() {
			this.cloudflareAccounts.push(this.normalizeCloudflareAccount({}))
			this.cloudflareExpandedIdx = this.cloudflareAccounts.length - 1
		},
		removeCloudflareAccount(idx) {
			this.cloudflareAccounts.splice(idx, 1)
			if (this.cloudflareExpandedIdx === idx) {
				this.cloudflareExpandedIdx = null
			} else if (this.cloudflareExpandedIdx !== null && this.cloudflareExpandedIdx > idx) {
				this.cloudflareExpandedIdx = this.cloudflareExpandedIdx - 1
			}
		},
		async saveCloudflareAccounts() {
			this.savingCloudflare = true
			try {
				const payload = this.cloudflareAccounts.map(a => ({
					...a,
					api_token: a.api_token === '' ? '__keep__' : a.api_token,
					// Same round-trip rule as api_token: the server masks the
					// secret on read and swaps '__keep__' for the stored value.
					r2_secret_access_key: a.r2_secret_access_key === '' ? '__keep__' : a.r2_secret_access_key,
				}))
				await juniperApi.put('/settings/cloudflare_accounts', { value: payload })
				showGlobalToast('Cloudflare accounts saved', 'success')
				await this.loadCloudflareSettings()
			} catch (e) {
				console.error(e)
				showGlobalToast('Failed to save Cloudflare accounts', 'danger')
			} finally {
				this.savingCloudflare = false
			}
		},
		cloudflareDotClassFor(acct) {
			const status = this.cloudflareTestResults[acct.name]
			if (status === 'pending') return 'gh-dot--pending'
			if (status === 'success') return 'gh-dot--on'
			if (status === 'failed') return 'gh-dot--bad'
			return 'gh-dot--off'
		},
		cloudflareDotTitleFor(acct) {
			const status = this.cloudflareTestResults[acct.name]
			if (status === 'pending') return 'Testing connection…'
			if (status === 'success') return 'Connection OK'
			if (status === 'failed') return 'Connection failed'
			return 'Not tested yet'
		},
		async testCloudflareAccount(idx) {
			const acct = this.cloudflareAccounts[idx]
			if (!acct) return
			this.testingCloudflareIdx = idx
			try {
				const { data } = await juniperApi.post('/settings/cloudflare/test', { account: { ...acct } })
				const key = acct.name || `__unnamed_${idx}__`
				if (data && data.ok) {
					this.$set(this.cloudflareTestResults, key, 'success')
					showGlobalToast(data.message || 'Connection OK', 'success')
				} else {
					this.$set(this.cloudflareTestResults, key, 'failed')
					showGlobalToast((data && data.message) || 'Connection failed', 'danger')
				}
			} catch (e) {
				const key = acct.name || `__unnamed_${idx}__`
				this.$set(this.cloudflareTestResults, key, 'failed')
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Network error during test'
				showGlobalToast(msg, 'danger')
			} finally {
				this.testingCloudflareIdx = null
			}
		},
		async testCloudflareR2Account(idx) {
			const acct = this.cloudflareAccounts[idx]
			if (!acct) return
			this.testingR2Idx = idx
			try {
				const { data } = await juniperApi.post('/settings/cloudflare/test-r2', { account: { ...acct } })
				if (data && data.ok) {
					showGlobalToast(data.message || 'R2 bucket reachable', 'success')
				} else {
					showGlobalToast((data && data.message) || 'R2 test failed', 'danger')
				}
			} catch (e) {
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Network error during R2 test'
				showGlobalToast(msg, 'danger')
			} finally {
				this.testingR2Idx = null
			}
		},
		async testCloudflareAccountSilent(idx) {
			const acct = this.cloudflareAccounts[idx]
			if (!acct) return
			const key = acct.name || `__unnamed_${idx}__`
			if (!acct.account_id || !acct.api_token) {
				this.$set(this.cloudflareTestResults, key, 'failed')
				return
			}
			this.$set(this.cloudflareTestResults, key, 'pending')
			try {
				const { data } = await juniperApi.post('/settings/cloudflare/test', { account: { ...acct } })
				this.$set(this.cloudflareTestResults, key, data && data.ok ? 'success' : 'failed')
			} catch (e) {
				this.$set(this.cloudflareTestResults, key, 'failed')
			}
		},
		async testAllCloudflareAccounts() {
			if (this.cloudflareAccounts.length === 0) return
			await Promise.all(
				this.cloudflareAccounts.map((_, idx) => this.testCloudflareAccountSilent(idx))
			)
		},
		truncateMid(s, n) {
			if (!s || s.length <= n) return s
			const head = Math.ceil((n - 1) / 2)
			const tail = Math.floor((n - 1) / 2)
			return s.slice(0, head) + '…' + s.slice(-tail)
		},



		// ----- Plesk accounts -----
		async loadPleskSettings() {
			this.loadingPlesk = true
			try {
				const { data } = await juniperApi.get('/settings/plesk_accounts')
				const value = data && data.value
				this.pleskAccounts = Array.isArray(value) ? value.map(this.normalizePleskAccount) : []
				if (this.pleskExpandedIdx !== null && this.pleskExpandedIdx >= this.pleskAccounts.length) {
					this.pleskExpandedIdx = null
				}
			} catch (e) {
				if (!e || !e.response || e.response.status !== 404) {
					console.error('Failed to load Plesk accounts', e)
				}
				this.pleskAccounts = []
				this.pleskExpandedIdx = null
			} finally {
				this.loadingPlesk = false
			}
			if (this.activeTab === 'plesk' && this.pleskAccounts.length > 0) {
				this.testAllPleskAccounts()
			}
		},
		normalizePleskAccount(a) {
			return Object.assign({ name: '', cli_url: '', ip: '', auth_token: '' }, a || {})
		},
		togglePleskAccount(idx) {
			this.pleskExpandedIdx = this.pleskExpandedIdx === idx ? null : idx
		},
		addPleskAccount() {
			this.pleskAccounts.push(this.normalizePleskAccount({}))
			this.pleskExpandedIdx = this.pleskAccounts.length - 1
		},
		removePleskAccount(idx) {
			this.pleskAccounts.splice(idx, 1)
			if (this.pleskExpandedIdx === idx) {
				this.pleskExpandedIdx = null
			} else if (this.pleskExpandedIdx !== null && this.pleskExpandedIdx > idx) {
				this.pleskExpandedIdx = this.pleskExpandedIdx - 1
			}
		},
		async savePleskAccounts() {
			this.savingPlesk = true
			try {
				const payload = this.pleskAccounts.map(a => ({
					...a,
					auth_token: a.auth_token === '' ? '__keep__' : a.auth_token,
				}))
				await juniperApi.put('/settings/plesk_accounts', { value: payload })
				showGlobalToast('Plesk accounts saved', 'success')
				await this.loadPleskSettings()
			} catch (e) {
				console.error(e)
				showGlobalToast('Failed to save Plesk accounts', 'danger')
			} finally {
				this.savingPlesk = false
			}
		},
		pleskDotClassFor(acct) {
			const status = this.pleskTestResults[acct.name]
			if (status === 'pending') return 'gh-dot--pending'
			if (status === 'success') return 'gh-dot--on'
			if (status === 'failed') return 'gh-dot--bad'
			return 'gh-dot--off'
		},
		pleskDotTitleFor(acct) {
			const status = this.pleskTestResults[acct.name]
			if (status === 'pending') return 'Testing connection…'
			if (status === 'success') return 'Connection OK'
			if (status === 'failed') return 'Connection failed'
			return 'Not tested yet'
		},
		async testPleskAccount(idx) {
			const acct = this.pleskAccounts[idx]
			if (!acct) return
			this.testingPleskIdx = idx
			try {
				const { data } = await juniperApi.post('/settings/plesk/test', { account: { ...acct } })
				const key = acct.name || `__unnamed_${idx}__`
				if (data && data.ok) {
					this.$set(this.pleskTestResults, key, 'success')
					showGlobalToast(data.message || 'Connection OK', 'success')
				} else {
					this.$set(this.pleskTestResults, key, 'failed')
					showGlobalToast((data && data.message) || 'Connection failed', 'danger')
				}
			} catch (e) {
				const key = acct.name || `__unnamed_${idx}__`
				this.$set(this.pleskTestResults, key, 'failed')
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Network error during test'
				showGlobalToast(msg, 'danger')
			} finally {
				this.testingPleskIdx = null
			}
		},
		async testPleskAccountSilent(idx) {
			const acct = this.pleskAccounts[idx]
			if (!acct) return
			const key = acct.name || `__unnamed_${idx}__`
			if (!acct.cli_url || !acct.auth_token) {
				this.$set(this.pleskTestResults, key, 'failed')
				return
			}
			this.$set(this.pleskTestResults, key, 'pending')
			try {
				const { data } = await juniperApi.post('/settings/plesk/test', { account: { ...acct } })
				this.$set(this.pleskTestResults, key, data && data.ok ? 'success' : 'failed')
			} catch (e) {
				this.$set(this.pleskTestResults, key, 'failed')
			}
		},
		async testAllPleskAccounts() {
			if (this.pleskAccounts.length === 0) return
			await Promise.all(
				this.pleskAccounts.map((_, idx) => this.testPleskAccountSilent(idx))
			)
		},
		shortPleskUrl(url) {
			if (!url) return ''
			return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
		},

		// ----- WAVE -----
		async loadWaveSettings() {
			try {
				const response = await api.get('/juniper/ada-auditor/settings')
				if (response.data) {
					this.waveCredits = response.data.wave_credits_remaining
					this.waveApiKeySet = !!response.data.wave_api_key_set
					this.waveApiKey = ''
					this.adaEngineUrl = response.data.ada_engine_url || ''
					this.pdfAuditorUrl = response.data.pdf_auditor_url || ''
					this.adaSlackWebhookUrl = response.data.ada_slack_webhook_url || ''
					if (response.data.launch_blockers) this.launchBlockers = { ...this.launchBlockers, ...response.data.launch_blockers }
				}
			} catch (error) {
				console.error('Failed to load WAVE settings:', error)
			}
		},
		async saveWaveSettings() {
			this.savingWave = true
			try {
				const payload = {
					// Always sent (not secrets) — empty clears the override → env default.
					ada_engine_url: this.adaEngineUrl.trim(),
					pdf_auditor_url: this.pdfAuditorUrl.trim(),
					ada_slack_webhook_url: this.adaSlackWebhookUrl.trim(),
					launch_blockers: this.launchBlockers,
				}
				if (this.waveApiKey) {
					payload.wave_api_key = this.waveApiKey
				}
				await api.post('/juniper/ada-auditor/settings', payload)
				showGlobalToast('Settings saved', 'success')
				this.waveApiKey = ''
				await this.loadWaveSettings()
			} catch (error) {
				showGlobalToast('Failed to save settings', 'danger')
			} finally {
				this.savingWave = false
			}
		},

		// ----- AI (provider keys + Theme Assistant) -----
		async loadAiSettings() {
			this.loadingAi = true
			try {
				const [providersRes, assistantRes, metaRes] = await Promise.all([
					juniperApi.get('/settings/ai_providers'),
					juniperApi.get('/settings/ai_theme_assistant'),
					juniperApi.get('/settings/ai/meta'),
				])

				// Secrets come back as SECRET_PLACEHOLDER when set — we only learn *whether* a key exists.
				const providers = (providersRes.data && providersRes.data.value) || {}
				this.aiKeysSet = {
					openai: !!providers.openai_api_key,
					anthropic: !!providers.anthropic_api_key,
				}
				this.aiKeys = { openai: '', anthropic: '' }
				this.aiKeysClear = { openai: false, anthropic: false }

				this.aiMeta = metaRes.data || this.aiMeta
				const defaults = this.aiMeta.defaults || {}
				const assistant = (assistantRes.data && assistantRes.data.value) || {}
				const provider = assistant.provider || defaults.provider
				this.ai = {
					provider: provider === 'anthropic' ? 'anthropic' : 'openai',
					openai_model: assistant.openai_model || '',
					openai_fallback_model: assistant.openai_fallback_model || '',
					anthropic_model: assistant.anthropic_model || '',
					max_tokens: assistant.max_tokens || null,
					// Show the default text when nothing custom is saved so it can be edited in place.
					system_prompt: assistant.system_prompt || this.aiMeta.default_system_prompt || '',
				}
			} catch (error) {
				console.error('Failed to load AI settings:', error)
			} finally {
				this.loadingAi = false
			}
		},
		// Live model ids per provider (needs a working key — stored or .env). Loaded
		// lazily on first visit to the tab and again after a save.
		async loadAiModels(force = false) {
			if (this.aiModelsLoaded && !force) return
			this.aiModelsLoaded = true
			await Promise.all(
				['openai', 'anthropic'].map(async provider => {
					try {
						const { data } = await juniperApi.get('/settings/ai/models', { params: { provider } })
						this.$set(this.aiModels, provider, data.models || [])
						this.$set(this.aiModelsError, provider, data.error || '')
					} catch (error) {
						this.$set(this.aiModels, provider, [])
						this.$set(this.aiModelsError, provider, 'Could not load the model list.')
					}
				})
			)
		},
		async saveAiSettings() {
			this.savingAi = true
			try {
				// Keys: new value → overwrite; "Remove key" → empty string clears it;
				// otherwise round-trip the placeholder so the stored secret is kept.
				const keys = {}
				;['openai', 'anthropic'].forEach(p => {
					if (this.aiKeysClear[p]) keys[p + '_api_key'] = ''
					else if (this.aiKeys[p].trim()) keys[p + '_api_key'] = this.aiKeys[p].trim()
					else keys[p + '_api_key'] = this.aiKeysSet[p] ? SECRET_PLACEHOLDER : ''
				})

				const prompt = (this.ai.system_prompt || '').trim()
				const defaultPrompt = (this.aiMeta.default_system_prompt || '').trim()
				const maxTokens = parseInt(this.ai.max_tokens, 10)
				const assistant = {
					provider: this.ai.provider,
					openai_model: (this.ai.openai_model || '').trim(),
					openai_fallback_model: (this.ai.openai_fallback_model || '').trim(),
					anthropic_model: (this.ai.anthropic_model || '').trim(),
					max_tokens: Number.isFinite(maxTokens) && maxTokens > 0 ? maxTokens : null,
					// Text identical to the built-in default = no customization, so future
					// default improvements keep flowing to this install.
					system_prompt: prompt === defaultPrompt ? '' : prompt,
				}

				await juniperApi.put('/settings/ai_providers', { value: keys })
				await juniperApi.put('/settings/ai_theme_assistant', { value: assistant })

				showGlobalToast('AI settings saved', 'success')
				this.aiTestResult = null
				await this.loadAiSettings()
				await this.loadAiModels(true)
			} catch (error) {
				console.error('Failed to save AI settings:', error)
				showGlobalToast('Failed to save AI settings', 'danger')
			} finally {
				this.savingAi = false
			}
		},
		async testAiConnection() {
			if (this.aiHasUnsavedKey) {
				showGlobalToast('Save the API key first, then test the connection.', 'info')
				return
			}
			this.testingAi = true
			this.aiTestResult = null
			try {
				const { data } = await juniperApi.post('/settings/ai/test', { provider: this.ai.provider, model: this.aiCurrentModel })
				this.aiTestResult = data
			} catch (error) {
				const body = (error.response && error.response.data) || {}
				this.aiTestResult = { ok: false, message: body.message || body.error || 'Test failed.' }
			} finally {
				this.testingAi = false
			}
		},
		resetAiPrompt() {
			this.ai.system_prompt = this.aiMeta.default_system_prompt || ''
		},
		clearAiKey(provider) {
			this.$set(this.aiKeysClear, provider, true)
			this.$set(this.aiKeys, provider, '')
		},
		undoClearAiKey(provider) {
			this.$set(this.aiKeysClear, provider, false)
		},
		aiKeySource(provider) {
			return this.aiMeta.effective ? this.aiMeta.effective[provider + '_key_source'] : null
		},
		aiKeySourceLabel(provider) {
			const source = this.aiKeySource(provider)
			if (source === 'settings') return 'Using the key saved here'
			if (source === 'env') return 'No key saved here — using the juniper-api server key'
			return 'No key configured — this provider cannot be used'
		},
		aiKeyDotClass(provider) {
			const source = this.aiKeySource(provider)
			if (source === 'settings') return 'gh-dot--on'
			if (source === 'env') return 'gh-dot--off'
			return 'gh-dot--bad'
		},

		// ----- GitHub accounts -----
		async loadGithubAccounts() {
			this.loadingGithub = true
			try {
				const response = await juniperApi.get('/settings/github_accounts')
				const value = response.data?.value
				this.githubAccounts = Array.isArray(value) ? value.map(this.normalizeAccount) : []
				// Collapse on reload so the user sees the full list of names first.
				if (this.expandedIdx !== null && this.expandedIdx >= this.githubAccounts.length) {
					this.expandedIdx = null
				}
			} catch (error) {
				if (error?.response?.status !== 404) {
					console.error('Failed to load GitHub accounts:', error)
				}
				this.githubAccounts = []
				this.expandedIdx = null
			} finally {
				this.loadingGithub = false
			}

			// Auto-test on initial landing if we're already on the GitHub tab.
			if (this.activeTab === 'github' && this.githubAccounts.length > 0) {
				this.testAllAccounts()
			}
		},
		normalizeAccount(a) {
			return Object.assign(emptyGithubAccount(), a || {})
		},
		toggleAccount(idx) {
			// Single-open accordion: clicking the open one collapses it.
			this.expandedIdx = this.expandedIdx === idx ? null : idx
		},

		// Header dot reflects the most recent connection test:
		//   pending  — gray, pulsing
		//   success  — green
		//   failed   — red
		//   unknown  — gray (untested this session)
		dotClassFor(idx, acct) {
			const status = this.testResults[acct.name]
			if (status === 'pending') return 'gh-dot--pending'
			if (status === 'success') return 'gh-dot--on'
			if (status === 'failed') return 'gh-dot--bad'
			return 'gh-dot--off'
		},
		dotTitleFor(idx, acct) {
			const status = this.testResults[acct.name]
			if (status === 'pending') return 'Testing connection…'
			if (status === 'success') return 'Connection OK'
			if (status === 'failed') return 'Connection failed'
			return 'Not tested yet'
		},

		async testAccount(idx) {
			const acct = this.githubAccounts[idx]
			if (!acct) return
			this.testingIdx = idx
			try {
				const { data } = await juniperApi.post('/settings/github_accounts/test', {
					account: { ...acct },
				})
				const key = acct.name || `__unnamed_${idx}__`
				if (data?.ok) {
					this.$set(this.testResults, key, 'success')
					showGlobalToast(data.message || 'Connection OK', 'success')
				} else {
					this.$set(this.testResults, key, 'failed')
					showGlobalToast(data?.message || 'Connection failed', 'danger')
				}
			} catch (e) {
				const key = acct.name || `__unnamed_${idx}__`
				this.$set(this.testResults, key, 'failed')
				const msg = e?.response?.data?.message || 'Network error during test'
				showGlobalToast(msg, 'danger')
			} finally {
				this.testingIdx = null
			}
		},

		// Silent test (no toast) used by the bulk-test that runs on tab activation.
		// Returns void; updates testResults so the header dots flip.
		async testAccountSilent(idx) {
			const acct = this.githubAccounts[idx]
			if (!acct) return
			const key = acct.name || `__unnamed_${idx}__`
			// If the entry has no org + token-or-keep yet, skip — nothing to test.
			if (!acct.org || !acct.token) {
				this.$set(this.testResults, key, 'failed')
				return
			}
			this.$set(this.testResults, key, 'pending')
			try {
				const { data } = await juniperApi.post('/settings/github_accounts/test', {
					account: { ...acct },
				})
				this.$set(this.testResults, key, data?.ok ? 'success' : 'failed')
			} catch (e) {
				this.$set(this.testResults, key, 'failed')
			}
		},

		async testAllAccounts() {
			if (this.testingAll || this.githubAccounts.length === 0) return
			this.testingAll = true
			try {
				await Promise.all(
					this.githubAccounts.map((_, idx) => this.testAccountSilent(idx))
				)
			} finally {
				this.testingAll = false
			}
		},
		addGithubAccount() {
			this.githubAccounts.push(emptyGithubAccount())
			// Auto-expand the newly added row so the user can fill it in immediately.
			this.expandedIdx = this.githubAccounts.length - 1
		},
		removeGithubAccount(idx) {
			this.githubAccounts.splice(idx, 1)
			// Collapse if the removed item was open, or shift the index down if a
			// later item was open and the removal moved it.
			if (this.expandedIdx === idx) {
				this.expandedIdx = null
			} else if (this.expandedIdx !== null && this.expandedIdx > idx) {
				this.expandedIdx = this.expandedIdx - 1
			}
		},
		async saveGithubAccounts() {
			this.savingGithub = true
			try {
				// Empty token field on a saved account means "keep existing".
				const payload = this.githubAccounts.map(a => ({
					...a,
					token: a.token === '' ? SECRET_PLACEHOLDER : a.token,
				}))
				await juniperApi.put('/settings/github_accounts', { value: payload })
				showGlobalToast('GitHub settings saved', 'success')
				await this.loadGithubAccounts()
			} catch (error) {
				console.error(error)
				showGlobalToast('Failed to save GitHub settings', 'danger')
			} finally {
				this.savingGithub = false
			}
		},

		// ----- Developers tab — shell command runner -----
		async executeDevCommand() {
			if (this.devExecuting) return
			this.devExecuting = true
			try {
				if (this.devCommand === 'cls') {
					this.devResponse = ''
				} else {
					const res = await juniperApi.post('/developers/execute-shell-command', { command: this.devCommand })
					this.devResponse +=
						'<span class="bg-danger text-dark">command:</span> ' + this.devCommand + '\n' + res.data + '\n\r'
					if (this.$swal) {
						this.$swal.fire({ icon: 'success', title: 'Command executed!', timer: 4000 })
					}
				}
				this.devCommandsHistory.push(this.devCommand)
				this.devCommand = ''
			} catch (error) {
				let message = ''
				if (error?.response?.data?.error) {
					for (const [, value] of Object.entries(error.response.data.error)) {
						message += ` ${value} \n`
					}
				}
				if (error?.response?.data?.message) {
					message = error.response.data.message
				}
				if (this.$swal) {
					this.$swal.fire({ icon: 'error', title: message || 'Command failed', timer: 6000 })
				}
			} finally {
				this.devExecuting = false
			}
		},
	},
	mixins: [juniperUserMixin],
}
</script>

<style scoped lang="scss">
$gh-border: #e6e8ec;
$gh-border-soft: #f0f1f4;
$gh-text: #2b303b;
$gh-text-soft: #4a5260;
$gh-text-muted: #8a909c;
$gh-bg: #fff;
$gh-bg-soft: #f8f9fb;
$gh-bg-hover: #f3f5f9;
$gh-accent: #2b6cff;
$gh-accent-soft: #ebf2ff;
$gh-success: #1ec872;
$gh-danger: #c93434;
$gh-danger-soft: #ffe9e9;

.js-tab-content {
	padding: 15px;
}

.gh-layout {
	display: grid;
	grid-template-columns: 30% 1fr;
	gap: 1.5rem;
	align-items: start;
}

/* Scoped override for Bootstrap cards used in the other tabs (Default, ADA) —
   keep the border and rounded corners, drop the solid white fill so they sit
   on the page background. */
::v-deep .card {
	background-color: transparent !important;
	border-color: $gh-border;
	border-radius: 14px;
}

::v-deep .card-header {
	background-color: transparent !important;
	border-bottom-color: $gh-border-soft;
}

/* ---- LEFT: intro (no border, no background) ---- */

.gh-intro {
	padding: 0.25rem 1rem 0 0;
}

.gh-intro__title {
	font-size: 1.5rem;
	font-weight: 700;
	color: $gh-text;
	margin: 0 0 0.5rem;
	letter-spacing: -0.01em;
}

.gh-intro__desc {
	font-size: 0.85rem;
	color: $gh-text-muted;
	line-height: 1.5;
	margin: 0;

	code {
		background: $gh-bg-hover;
		padding: 0.05rem 0.3rem;
		border-radius: 4px;
		font-size: 0.75rem;
	}
}

/* ---- RIGHT: accordion list of accounts ---- */

.gh-accounts {
	display: flex;
	flex-direction: column;
}

/* Empty-state container — bordered card, vertically stacked content:
   GitHub mark, descriptive copy, and the same "+ Add account" link used
   below the accordion when accounts exist. */
.gh-create-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	padding: 1.75rem 1.5rem;
	border: 1px solid $gh-border;
	border-radius: 14px;
	background: transparent;
	text-align: center;
}

.gh-create-card__icon {
	font-size: 2.25rem;
	color: #cbd0d8;
}

.gh-create-card__desc {
	font-size: 0.85rem;
	color: $gh-text-muted;
	max-width: 26rem;
	margin: 0;
	line-height: 1.45;
}

/* Wrapper that centers the Add-account link below the accordion list. */
.gh-add-link-row {
	display: flex;
	justify-content: center;
	padding: 0.85rem 0 0.25rem;
}

/* Neutral "+ Add account" link: muted text, circled plus prefix. Reads as
   a subtle secondary action — the page's only real primary action is Save
   changes below the dividers. */
.gh-add-link {
	display: inline-flex;
	align-items: center;
	gap: 0.55rem;
	padding: 0.35rem 0.6rem;
	font-size: 0.95rem;
	font-weight: 600;
	color: $gh-text-muted;
	text-decoration: none;
	border-radius: 8px;
	transition: color 0.15s ease, background 0.15s ease;

	&:hover {
		color: $gh-text;
		background: $gh-bg-hover;
		text-decoration: none;

		.gh-add-link__circle {
			border-color: $gh-text-soft;
			color: $gh-text;
		}
	}

	&:focus-visible {
		outline: 2px solid $gh-accent;
		outline-offset: 2px;
	}
}

.gh-add-link__circle {
	width: 18px;
	height: 18px;
	border-radius: 50%;
	border: 1.5px solid $gh-border;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: $gh-text-muted;
	font-size: 0.6rem;
	flex-shrink: 0;
	transition: border-color 0.15s ease, color 0.15s ease;
}

.gh-divider {
	border: none;
	border-top: 1px solid #c8ccd4;
	margin: 1.5rem 15px;
}

.gh-save-row {
	display: flex;
	justify-content: flex-end;
}

/* ----- Default tab ----- */

/* Adoption stats list: each engine on its own row inside a soft bordered
   container, with the one matching `default_engine` highlighted. Reads as
   "X engine is used by N clients" without needing a chart. */
.def-stats {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.def-stats__row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.65rem 0.85rem;
	border: 1px solid $gh-border;
	border-radius: 10px;
	background: transparent;
	transition: border-color 0.15s ease, background 0.15s ease;

	&--active {
		border-color: lighten($gh-accent, 25%);
		background: $gh-accent-soft;
	}
}

.def-stats__name {
	font-weight: 600;
	font-size: 0.9rem;
	color: $gh-text;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.def-stats__badge {
	font-size: 0.65rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	padding: 0.15rem 0.4rem;
	border-radius: 4px;
	background: $gh-accent;
	color: #fff;
}

.def-stats__count {
	font-weight: 700;
	font-size: 1.05rem;
	color: $gh-text;
	font-variant-numeric: tabular-nums;
	display: flex;
	align-items: baseline;
	gap: 0.35rem;
}

.def-stats__count-label {
	font-size: 0.75rem;
	font-weight: 500;
	color: $gh-text-muted;
}

/* ----- ADA Compliance tab ----- */

/* Single bordered container for the WAVE config — mirrors the look of an
   open accordion item from the GitHub tab, but without the collapsible
   header (only one logical section here). */
.ada-card {
	border: 1px solid $gh-border;
	border-radius: 12px;
	padding: 1.25rem 1.15rem;
	background: transparent;
}

/* Input + inline show/hide icon button, used for the masked WAVE API key. */
.ada-input-wrap {
	position: relative;
}

.ada-input-wrap .form-control {
	padding-right: 2.6rem;
}

.ada-input-action {
	position: absolute;
	right: 0.4rem;
	top: 50%;
	transform: translateY(-50%);
	background: transparent;
	border: none;
	color: $gh-text-muted;
	padding: 0.3rem 0.55rem;
	border-radius: 6px;
	cursor: pointer;
	transition: background 0.12s ease, color 0.12s ease;

	&:hover {
		background: $gh-bg-hover;
		color: $gh-text;
	}

	&:focus-visible {
		outline: 2px solid $gh-accent;
		outline-offset: 1px;
	}
}

.ada-credits {
	display: flex;
	align-items: center;
	gap: 0.55rem;
	padding: 0.2rem 0;
}

.ada-credits__count {
	font-size: 1.1rem;
	font-weight: 700;
	color: $gh-text;
	font-variant-numeric: tabular-nums;
}

.ada-credits__label {
	font-size: 0.85rem;
	color: $gh-text-muted;
}

/* Save changes — neutral style: black text, transparent fill, gray border. */
.gh-save-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.35rem 0.9rem;
	background: transparent;
	border: 1px solid $gh-border;
	border-radius: 10px;
	color: $gh-text;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

	&:hover:not(:disabled) {
		background: $gh-bg-hover;
		border-color: darken($gh-border, 6%);
	}

	&:focus-visible {
		outline: 2px solid $gh-accent;
		outline-offset: 2px;
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
}

.gh-accordion {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.6rem;
}

.gh-accordion__item {
	border: 1px solid $gh-border;
	border-radius: 12px;
	overflow: hidden;
	background: transparent;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;

	&--open {
		border-color: lighten($gh-accent, 25%);
		box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.08);
	}
}

.gh-accordion__head {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 0.85rem;
	padding: 0.85rem 1rem;
	background: transparent;
	border: none;
	cursor: pointer;
	text-align: left;
	transition: background 0.12s ease;

	&:hover { background: $gh-bg-hover; }
	&:focus-visible { outline: 2px solid $gh-accent; outline-offset: -2px; }
}

.gh-accordion__avatar {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: $gh-text;
	color: #fff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	font-size: 1rem;
}

.gh-accordion__head-body {
	min-width: 0;
	flex: 1;
}

.gh-accordion__name {
	font-weight: 600;
	font-size: 0.95rem;
	color: $gh-text;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.gh-accordion__meta {
	font-size: 0.78rem;
	color: $gh-text-muted;
	display: flex;
	gap: 0.4rem;
	align-items: center;
	margin-top: 0.1rem;
}

.gh-accordion__sep {
	color: #cbd0d8;
}

.gh-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;

	&--on { background: $gh-success; }
	&--off { background: #cbd0d8; }
	&--bad { background: $gh-danger; }
	&--pending {
		background: #cbd0d8;
		animation: gh-dot-pulse 1s ease-in-out infinite;
	}
}

@keyframes gh-dot-pulse {
	0%, 100% { opacity: 0.4; }
	50% { opacity: 1; }
}

.gh-accordion__chevron {
	color: $gh-text-muted;
	font-size: 0.8rem;
	transition: transform 0.2s ease;
	flex-shrink: 0;

	.gh-accordion__item--open & {
		transform: rotate(180deg);
	}
}

.gh-accordion__body {
	padding: 0.25rem 1.15rem 1.25rem;
	border-top: 1px solid $gh-border-soft;
	animation: gh-accordion-in 0.2s ease;
}

@keyframes gh-accordion-in {
	from { opacity: 0; transform: translateY(-4px); }
	to { opacity: 1; transform: translateY(0); }
}

.gh-accordion__actions {
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
	padding-top: 0.5rem;
	margin-top: 1rem;
	border-top: 1px solid $gh-border-soft;
}


.gh-section {
	margin-bottom: 1.85rem;

	&:last-child { margin-bottom: 0; }
}

.gh-section__title {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: $gh-text-muted;
	margin-bottom: 0.75rem;
}

.gh-section__hint {
	font-weight: 500;
	text-transform: none;
	letter-spacing: 0;
	color: #b6bcc8;
}

/* Segmented control for choosing Organization vs Personal. Behaves like a
   radio group but reads visually as two pill-buttons side by side, with the
   active one filled and bordered in accent. */
/* Launch blockers — plain checkbox rows (ADA Compliance tab) */
.gh-field .launch-blocker {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin: 0 0 0.35rem;
	font-size: 0.9rem;
	font-weight: 400;
	color: inherit;
	cursor: pointer;
}
.gh-field .launch-blocker--scope {
	margin-top: 0.6rem;
	padding-top: 0.6rem;
	border-top: 1px solid #f1f3f5;
}
.gh-field .launch-blocker input {
	margin: 0;
}

.gh-type-toggle {
	display: inline-flex;
	gap: 0.5rem;
	flex-wrap: wrap;
}

.gh-type-toggle__opt {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.55rem 1rem;
	border: 1px solid $gh-border;
	border-radius: 10px;
	background: transparent;
	color: $gh-text-soft;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
	user-select: none;
	transition: all 0.12s ease;

	input { display: none; }
	i { color: $gh-text-muted; transition: color 0.12s ease; }

	&:hover {
		background: $gh-bg-hover;
		color: $gh-text;
		i { color: $gh-text; }
	}

	&--active {
		background: $gh-accent-soft;
		border-color: lighten($gh-accent, 25%);
		color: $gh-accent;
		i { color: $gh-accent; }

		&:hover {
			background: $gh-accent-soft;
			color: $gh-accent;
			i { color: $gh-accent; }
		}
	}
}

.gh-grid {
	display: grid;
	gap: 0.85rem;

	&--2 {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

.gh-field {
	margin-bottom: 0.85rem;

	&:last-child { margin-bottom: 0; }

	label {
		font-size: 0.78rem;
		font-weight: 600;
		color: $gh-text-soft;
		margin-bottom: 0.3rem;
		display: block;
	}

	::v-deep .form-control,
	::v-deep .form-select,
	.form-control,
	.form-select {
		border-radius: 9px;
		border-color: $gh-border;
		font-size: 0.9rem;
		padding: 0.5rem 0.75rem;
		transition: border-color 0.12s, box-shadow 0.12s;

		&:focus {
			border-color: $gh-accent;
			box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
		}
	}
}

.gh-help {
	font-size: 0.75rem;
	color: $gh-text-muted;
	margin-top: 0.4rem;

	code {
		background: $gh-bg-hover;
		padding: 0.05rem 0.3rem;
		border-radius: 4px;
		font-size: 0.72rem;
	}
}

.gh-toggle {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	padding: 0.85rem 1rem;
	background: $gh-bg-soft;
	border-radius: 10px;
	margin-top: 0.85rem;
}

.gh-toggle__label {
	font-weight: 600;
	font-size: 0.9rem;
	color: $gh-text;
}

.gh-toggle__help {
	font-size: 0.75rem;
	color: $gh-text-muted;
	margin-top: 0.15rem;

	code {
		background: #fff;
		border: 1px solid $gh-border-soft;
		padding: 0.05rem 0.3rem;
		border-radius: 4px;
		font-size: 0.72rem;
	}
}

.gh-switch {
	position: relative;
	display: inline-block;
	width: 46px;
	height: 26px;
	flex-shrink: 0;
	cursor: pointer;

	input {
		opacity: 0;
		width: 0;
		height: 0;
	}

	&__slider {
		position: absolute;
		inset: 0;
		background: #cbd0d8;
		border-radius: 26px;
		transition: background 0.2s;

		&::before {
			content: '';
			position: absolute;
			width: 20px;
			height: 20px;
			left: 3px;
			bottom: 3px;
			background: #fff;
			border-radius: 50%;
			transition: transform 0.2s;
			box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
		}
	}

	input:checked + &__slider {
		background: $gh-success;

		&::before {
			transform: translateX(20px);
		}
	}
}

.gh-editor__footer {
	padding: 0.85rem 1.85rem;
	border-top: 1px solid $gh-border-soft;
	background: $gh-bg-soft;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
}

@media (max-width: 991px) {
	.gh-layout {
		grid-template-columns: 1fr;
	}

	.gh-sidebar,
	.gh-editor {
		min-height: 0;
	}
}

/* ----- AI tab ----- */

/* Compact variant of the flat button for in-card actions (Test connection). */
.gh-save-btn--sm {
	padding: 0.25rem 0.6rem;
	font-size: 0.78rem;
	border-radius: 7px;
	gap: 0.3rem;
}

/* Second 30/70 block on the AI tab (Theme Assistant), stacked under AI Providers. */
.ai-layout {
	margin-top: 2rem;
}

.ai-key-status {
	display: flex;
	align-items: center;
	gap: 0.45rem;
	flex-wrap: wrap;

	a { color: $gh-accent; }
}

.ai-effective {
	margin: -0.25rem 0 0.9rem;
}

.ai-test-row {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
	min-height: 2.35rem;
}

.ai-test-result {
	display: inline-flex;
	align-items: center;
	gap: 0.45rem;
	font-size: 0.8rem;
	color: $gh-text-soft;

	&--bad { color: $gh-danger; }
}

.gh-field ::v-deep .ai-prompt,
.gh-field .ai-prompt {
	font-family: SFMono-Regular, Menlo, Consolas, monospace;
	font-size: 0.78rem;
	line-height: 1.45;
	color: $gh-text-soft;
	resize: vertical;
}

.gh-help a { color: $gh-accent; }

/* ----- Developers tab ----- */

.dev-runner {
	padding: 1rem 1.15rem;
}

.dev-history {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.dev-history__item {
	cursor: pointer;
	padding: 4px 8px;
	border-radius: 8px;
	border: 1px solid $gh-border;
	background: $gh-bg-soft;
	font-size: 12px;
	color: $gh-text-soft;
	transition: background 0.12s ease, border-color 0.12s ease;

	&:hover {
		background: $gh-bg-hover;
		border-color: darken($gh-border, 6%);
	}
}

.dev-console {
	border: 1px solid #1f2937;
	border-radius: 14px;
	overflow: hidden;
	background: #0b1220;
}

.dev-console__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px;
	font-size: 12px;
	font-weight: 600;
	color: #cbd5e1;
	border-bottom: 1px solid #1e293b;
	background: #111827;
}

.dev-console__clear {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: transparent;
	border: 1px solid #334155;
	color: #cbd5e1;
	font-size: 11px;
	font-weight: 500;
	padding: 3px 8px;
	border-radius: 6px;
	cursor: pointer;
	transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

	&:hover:not(:disabled) {
		background: #1e293b;
		border-color: #475569;
		color: #f1f5f9;
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
}

.dev-console__body {
	margin: 0;
	padding: 12px;
	color: #e2e8f0;
	background: #0b1220;
	height: calc(100vh * 0.55);
	overflow: auto;
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 12px;
}

/* ── PMs tab ─────────────────────────────────────────────────────── */
.pm-count {
	float: right;
	font-weight: 400;
	text-transform: none;
	letter-spacing: normal;
	color: #adb5bd;
}

.pm-search {
	position: relative;
	margin-bottom: 10px;

	.form-control {
		padding-left: 32px;
		font-size: 13px;
	}
}

.pm-search__icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	font-size: 12px;
	color: #adb5bd;
	pointer-events: none;
}

.pm-list {
	max-height: 420px;
	overflow-y: auto;
}

.pm-row {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 7px 4px;
	margin: 0;
	font-size: 13px;
	border-bottom: 1px solid #f1f3f5;
	cursor: pointer;

	&:last-of-type {
		border-bottom: none;
	}

	&:hover {
		background: #f8f9fb;
	}

	input[type='checkbox'] {
		margin: 0;
		accent-color: #111827;
		cursor: pointer;
		flex-shrink: 0;
	}
}

.pm-row__avatar {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
}

.pm-row__avatar--placeholder {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: #f1f3f5;
	color: #adb5bd;
	font-size: 11px;
}

.pm-row__name {
	font-weight: 500;
	color: #111827;
	white-space: nowrap;
}

.pm-row__email {
	color: #adb5bd;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.pm-empty {
	padding: 10px 4px;
	font-size: 13px;
}
</style>
